// Remote push notifications (APNs on iOS, FCM on Android).
// We ask for permission once the user has signed in, not at cold start, and
// send the device token to the backend so it can target this user later.
// All plugin calls are guarded, so this is a no-op on web.

import axios from "axios";
import { getPlatform, isNativeApp } from "./platform";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Android's register() needs Firebase (google-services.json); calling it without
// that crashes the app, so Android only registers once FCM is set up.
const ANDROID_FCM_ENABLED = process.env.REACT_APP_ANDROID_FCM_ENABLED === "true";

let listenersAttached = false;

async function attachListeners(PushNotifications, onOpenRoute) {
  if (listenersAttached) return;
  listenersAttached = true;
  await PushNotifications.addListener("registration", async ({ value }) => {
    try {
      await axios.post(`${API}/push/register`, { token: value, platform: getPlatform() });
    } catch { /* retried on next launch */ }
  });
  await PushNotifications.addListener("registrationError", (err) => {
    console.warn("Push registration failed", err);
  });
  await PushNotifications.addListener("pushNotificationActionPerformed", (event) => {
    const route = event?.notification?.data?.route;
    if (route && typeof onOpenRoute === "function") onOpenRoute(route);
  });
}

// Call after sign-in. Shows the OS permission prompt the first time only;
// afterwards it just refreshes the device token if permission was granted.
export async function setupPushNotifications(onOpenRoute) {
  if (!isNativeApp()) return;
  const platform = getPlatform();
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") return;
    if (platform === "android" && !ANDROID_FCM_ENABLED) return;
    await attachListeners(PushNotifications, onOpenRoute);
    await PushNotifications.register();
  } catch (e) {
    console.warn("Push setup failed", e);
  }
}
